import React, { useContext, useEffect, useState } from 'react'
import { AppContext } from '@state/context'
import ProgressBar from '@components/ProgressBar'
import Divider from '@components/Divider'
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  CONTAINER_STYLE,
  DIVIDER_SIZE,
  EXAM_QUESTION_ORDER_TYPES,
  HEADING_TYPE,
  TEXT_SIZES,
  USER_ROLES,
} from '@helpers/constants'
import Text from '@components/Text'
import BlockRenderer from '@views/BlockRenderer'
import Form from '@components/Form'
import Button from '@components/Button'
import ValidateQuizExamAnswers from '@components/ValidateQuizExamAnswers'
import Heading from '@components/Heading'
import { shuffleArray } from '@helpers/utilities'
import Loader from '@components/Loader'
import { useUpdateExamAttempt } from '@hooks/mutations/useUpdateExamAttempt'
import { useCreateExamAttempt } from '@hooks/mutations/useCreateExamAttempt'
import ButtonWithLoader from '@components/ButtonWithLoader'
import {
  onCloseExam,
  setCurrentSessionLatestFinishedExam,
  setCurrentExamAttemptInProgressId,
} from '@state/actions/appActions'
import { getAttemptsLeft } from '@helpers/exams'

import './style.css'
import Container from '@components/Container'

const pickRandomAnswers = (
  answers,
  answerDisplayCount,
  showAllRightAnswers = false
) => {
  const correctAnswers = answers.filter((answer) => answer.correct)
  const incorrectAnswers = answers.filter((answer) => !answer.correct)

  let selectedAnswers = []

  if (showAllRightAnswers) {
    // If there are more correct answers than the display count, shuffle and slice
    if (correctAnswers.length >= answerDisplayCount) {
      selectedAnswers = shuffleArray(correctAnswers).slice(
        0,
        answerDisplayCount
      )
    } else {
      // Otherwise, include all correct answers and fill up with incorrect ones
      selectedAnswers = [...correctAnswers]
      const remainingCount = answerDisplayCount - selectedAnswers.length
      selectedAnswers.push(
        ...shuffleArray(incorrectAnswers).slice(0, remainingCount)
      )
    }
  } else {
    // Ensure at least one correct answer
    if (correctAnswers.length > 0) {
      selectedAnswers.push(
        correctAnswers[Math.floor(Math.random() * correctAnswers.length)]
      )
    }

    // Pick remaining answers from incorrect ones, ensuring total count matches answerDisplayCount
    const remainingAnswers = [...incorrectAnswers, ...correctAnswers].filter(
      (answer) => !selectedAnswers.includes(answer)
    )

    const additionalAnswers = shuffleArray(remainingAnswers).slice(
      0,
      answerDisplayCount - selectedAnswers.length
    )

    selectedAnswers.push(...additionalAnswers)
  }

  // Shuffle the final selected answers
  return shuffleArray(selectedAnswers)
}

// Builds the question list for one attempt: optionally shuffles the
// questions, then limits and shuffles the answers of each question.
const buildQuestions = (exam) => {
  const contentObj = JSON.parse(exam.content)

  if (!contentObj?.questions) return []

  let questions = contentObj.questions

  // 1. Randomize questions?
  if (
    exam.questionOrderType === parseInt(EXAM_QUESTION_ORDER_TYPES.RANDOM.value)
  ) {
    questions = shuffleArray([...questions])
  }

  // 2. Limit answer count, and randomize answers
  return questions.map((question) => {
    const updatedAnswers = pickRandomAnswers(
      question.answers,
      exam.answerDisplayCount,
      exam.showAllRightAnswers
    )
    return { ...question, answers: updatedAnswers }
  })
}

const areSelectedAnswersCorrect = (selectedAnswers, answers) => {
  // Ensure all selected answers are correct and the number of selected matches the number of correct answers
  return (
    selectedAnswers.every((index) => answers[index].correct) &&
    selectedAnswers.length === answers.filter((a) => a.correct).length
  )
}

const QuizExamView = (props) => {
  const { fullScreen } = props

  const [state, dispatch] = useContext(AppContext)

  const {
    app: { userId, userRole, currentActiveExam, currentActiveExamAttemptId },
  } = state

  const [questions, setQuestions] = useState([])

  // Attempts created from this view via "Gör om test". The exam object in
  // state is a snapshot from when the exam was opened, so its customerAttempts
  // does not include the attempt in progress nor any retries made since.
  const [retryCount, setRetryCount] = useState(0)

  const [atQuestion, setAtQuestion] = useState(0)
  const [score, setScore] = useState(0)
  const [passingPercentage, setPassingPercentage] = useState(0)
  const [correctedAnswers, setCorrectedAnswers] = useState([])
  const [selectedAnswers, setSelectedAnswers] = useState([])

  // const type = 'quizQuestions' // Går igenom frågorna och visar svar på slutet
  const type = 'quizExercise' // Visar rätt svar direkt efter varje fråga och kräver ett till klick för att gå vidare

  const inShowAnswerState = type === 'quizExercise'
  const [showAnswer, setShowAnswer] = useState(false)
  const [inReview, setInReview] = useState(false)

  // if no selected answers, disable button
  const disableButton = selectedAnswers[atQuestion]?.length === 0
  const { question, answers } = questions[atQuestion]
    ? questions[atQuestion]
    : { question: null, answers: [] }

  const registerAnswer = (index) => {
    const newSelectedAnswers = [...selectedAnswers]
    const currentSelection = newSelectedAnswers[atQuestion] || []

    if (currentSelection.includes(index)) {
      // Deselect the answer
      newSelectedAnswers[atQuestion] = currentSelection.filter(
        (i) => i !== index
      )
    } else {
      // Select the answer
      newSelectedAnswers[atQuestion] = [...currentSelection, index]
    }

    setSelectedAnswers(newSelectedAnswers)
  }

  // restarts the quiz, all states reset.
  const restartQuiz = () => {
    setInReview(false)
    setShowAnswer(false)
    setAtQuestion(0)
    setScore(0)
    setCorrectedAnswers([])
    setSelectedAnswers(questions.map(() => []))
  }

  // --- Retry: create a new attempt and start over with freshly picked questions

  const { post: postCreate, isPosting: isPostingCreate } =
    useCreateExamAttempt()

  const retryExam = async () => {
    if (userRole !== USER_ROLES.STUDENT || !currentActiveExam) return

    try {
      const res = await postCreate({
        exam: currentActiveExam.id,
        customer: userId,
        correctCount: 0,
        incorrectCount: 0,
        totalCount: questions.length,
      })

      if (res.success) {
        setRetryCount(retryCount + 1)
        setQuestions(buildQuestions(currentActiveExam))
        restartQuiz()
      }
    } catch (error) {
      console.log('🚀 ~ retryExam ~ error:', error)
    }
  }

  // review questions
  const reviewQuestions = () => {
    setInReview(true)
    setShowAnswer(true)
    setAtQuestion(0)
  }

  // All must be correct to yield a point
  const nextQuestion = async () => {
    if (!inReview) {
      await updateExamAttempt()
    }

    const currentAnswers = selectedAnswers[atQuestion] || []

    // Use the helper function to check if the selected answers are correct
    const isCorrect = areSelectedAnswersCorrect(currentAnswers, answers)

    if (isCorrect) {
      setScore(score + 1)
    }

    setAtQuestion(atQuestion + 1)
    if (!inReview) setShowAnswer(false)

    if (atQuestion === questions.length - 1) {
      const corrected = questions.map(({ answers }, index) => {
        const selected = selectedAnswers[index] || []
        return areSelectedAnswersCorrect(selected, answers)
      })
      setCorrectedAnswers(corrected)
    }
  }

  const showValidateQuizAnswers = () => {
    if (inReview) return false

    if (inShowAnswerState) {
      return !showAnswer && correctedAnswers.length >= questions.length
    } else {
      return correctedAnswers.length >= questions.length
    }
  }

  const getMarkCorrect = (index) => {
    if (!inShowAnswerState && !inReview) return false

    // Check if the answer is selected and correct
    if (
      selectedAnswers[atQuestion]?.includes(index) &&
      correctedAnswers[atQuestion] === true
    ) {
      return true
    }

    // If we're in review mode or showing the answer, mark the correct answer
    if (showAnswer && questions[atQuestion]?.answers[index]?.correct) {
      return true
    }

    return false
  }

  const getMarkIncorrect = (index) => {
    if (!inShowAnswerState && !inReview) return false

    // Only mark as incorrect if the answer is selected and incorrect
    if (
      selectedAnswers[atQuestion]?.includes(index) &&
      correctedAnswers[atQuestion] === false &&
      !questions[atQuestion]?.answers[index]?.correct
    ) {
      return true
    }

    return false
  }

  // --- Set local data from exam

  useEffect(() => {
    if (currentActiveExam?.content) {
      const questions = buildQuestions(currentActiveExam)

      setQuestions(questions)
      setSelectedAnswers(questions.map(() => []))
      setRetryCount(0)

      // Set passing percentage
      setPassingPercentage(currentActiveExam.passingPercentage)
    }
  }, [currentActiveExam])

  // --- Update attempt on progress

  const { post, isPosting } = useUpdateExamAttempt()

  const updateExamAttempt = async () => {
    if (
      userRole === USER_ROLES.STUDENT &&
      currentActiveExamAttemptId &&
      correctedAnswers.length > 0
    ) {
      const correctCount = correctedAnswers.filter((v) => v === true).length
      const incorrectCount = correctedAnswers.filter((v) => v === false).length

      const postData = {
        id: currentActiveExamAttemptId,
        correctCount: correctCount,
        incorrectCount: incorrectCount,
      }

      // All questions answered, evaluate if user passed
      const allQuestionsAnswered = correctedAnswers.length === questions.length
      if (allQuestionsAnswered) {
        const percent = (correctCount * 100) / questions.length
        const isPassed = percent >= passingPercentage

        postData['isPassed'] = isPassed
      }

      await post(postData)

      if (currentActiveExam && allQuestionsAnswered) {
        dispatch(setCurrentSessionLatestFinishedExam(currentActiveExam))
        dispatch(setCurrentExamAttemptInProgressId(null))
      }
    }
  }

  // ---

  const closeExam = () => {
    dispatch(onCloseExam())
  }

  // ---

  if (!currentActiveExam) {
    return null
  }

  if (userRole === USER_ROLES.STUDENT && !currentActiveExamAttemptId) {
    return <Loader />
  }

  const hasCorrectedCurrentAnswer = correctedAnswers.length > atQuestion
  const currentAnswerIsCorrect =
    hasCorrectedCurrentAnswer && correctedAnswers[atQuestion]
  const currentAnswerIsIncorrect =
    hasCorrectedCurrentAnswer && !correctedAnswers[atQuestion]

  const heading = (
    <div>
      <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
        {currentActiveExam?.name}
      </Heading>

      <Divider size={DIVIDER_SIZE.SMALL} line />
      <Divider size={DIVIDER_SIZE.SMALL} />
    </div>
  )

  const progressBar = (
    <div>
      <ProgressBar
        questionNum={questions.length}
        atQuestion={atQuestion}
        correctedAnswers={correctedAnswers}
      />

      {(currentAnswerIsCorrect || currentAnswerIsIncorrect) && (
        <Divider size={DIVIDER_SIZE.XSMALL} />
      )}
      {currentAnswerIsCorrect && (
        <Text extraClasses='text-[#5F9B56]'>Ditt svar är korrekt</Text>
      )}
      {currentAnswerIsIncorrect && (
        <Text extraClasses='text-[#EA3323]'>Ditt svar är inte korrekt</Text>
      )}

      <Divider size={DIVIDER_SIZE.SMALL} />
    </div>
  )

  const examContent = (
    <div>
      {atQuestion < questions.length && (
        <>
          <Text>
            Fråga {atQuestion + 1} av {questions.length}
          </Text>

          <div className='block-renderer-wrapper'>
            <BlockRenderer content={question} />
          </div>

          <Divider size={DIVIDER_SIZE.SMALL} />

          {answers.map((answerItem, index) => {
            const { text } = answerItem
            const textArea =
              answerItem.textArea && JSON.parse(answerItem.textArea)

            const checked = selectedAnswers[atQuestion]?.includes(index)
            const markCorrect = getMarkCorrect(index)
            const markIncorrect = getMarkIncorrect(index)

            let wrapperClasses =
              'inline-block px-3.5 -ml-3.5 border-solid rounded-full'

            if (checked || markCorrect || markIncorrect) {
              wrapperClasses += ' border'
            }

            if (markCorrect) {
              wrapperClasses += ' border-[#5F9B56] bg-[#5F9B56] bg-opacity-20'
            } else if (markIncorrect) {
              wrapperClasses += ' border-[#EA3323] bg-[#EA3323] bg-opacity-20'
            } else if (checked) {
              wrapperClasses += ' border-mediumGrey bg-lightGrey'
            }

            return (
              <div key={index} className={'mb-2'}>
                <div className={wrapperClasses}>
                  <div className={'flex'}>
                    <Form.QuizCheckbox
                      name={atQuestion + 'answer' + index}
                      id={atQuestion + 'answer' + index}
                      value={index}
                      checked={checked}
                      onChange={(e) => {
                        registerAnswer(index)
                      }}
                      disabled={showAnswer}
                      // markCorrect={markCorrect}
                      // markIncorrect={markIncorrect}
                    />
                    <label
                      className={showAnswer ? '' : 'cursor-pointer'}
                      htmlFor={atQuestion + 'answer' + index}
                    >
                      <BlockRenderer content={text} />
                    </label>
                  </div>
                </div>

                {type === 'quizExercise' &&
                  Object.keys(textArea).length > 0 &&
                  !textArea.isEmpty &&
                  showAnswer &&
                  questions[atQuestion].answers[index]?.correct && (
                    // <div className={`bg-lightGreen p-3 rounded-md mt-2 mb-4`}>
                    <div className='text-sm mt-1 mb-1'>
                      <BlockRenderer content={textArea} />
                    </div>
                  )}
              </div>
            )
          })}

          <Divider size={DIVIDER_SIZE.SMALL} />
        </>
      )}
    </div>
  )

  const buttons = (
    <div>
      {atQuestion < questions.length && (
        <>
          {atQuestion === questions.length - 1 && inReview && (
            // <Button
            //   disabled={disableButton}
            //   type={BUTTON_TYPE.SECONDARY}
            //   handleClick={restartQuiz}
            // >
            //   Gör om
            // </Button>
            <Button
              disabled={disableButton}
              type={BUTTON_TYPE.SECONDARY}
              handleClick={closeExam}
            >
              Stäng
            </Button>
          )}

          {type === 'quizExercise' && !showAnswer && (
            <Button
              disabled={disableButton}
              size={BUTTON_SIZE.MEDIUM}
              type={BUTTON_TYPE.PRIMARY}
              handleClick={() => {
                setShowAnswer(true)

                const isCorrect = areSelectedAnswersCorrect(
                  selectedAnswers[atQuestion],
                  answers
                )

                setCorrectedAnswers([...correctedAnswers, isCorrect])
              }}
            >
              Okej
            </Button>
          )}

          {atQuestion < questions.length - 1 &&
            (showAnswer || type === 'quizQuestions') && (
              <ButtonWithLoader
                // disabled={disableButton}
                type={BUTTON_TYPE.PRIMARY}
                size={BUTTON_SIZE.MEDIUM}
                handleClick={nextQuestion}
                isLoading={isPosting}
                disabledText={'Nästa fråga'}
                enabledText={'Nästa fråga'}
                disabled={isPosting}
              />
            )}

          {atQuestion === questions.length - 1 &&
            !inReview &&
            (showAnswer || type === 'quizQuestions') && (
              <ButtonWithLoader
                // disabled={disableButton}
                type={BUTTON_TYPE.PRIMARY}
                size={BUTTON_SIZE.MEDIUM}
                handleClick={nextQuestion}
                isLoading={isPosting}
                disabledText={'Slutför'}
                enabledText={'Slutför'}
                disabled={isPosting}
              />
            )}
        </>
      )}
    </div>
  )

  // Attempts used = attempts before opening + the one in progress + retries
  const attemptsUsed =
    (currentActiveExam.customerAttempts?.length ?? 0) + 1 + retryCount
  const canRetry =
    userRole === USER_ROLES.STUDENT &&
    getAttemptsLeft(currentActiveExam, attemptsUsed) > 0

  const answersValidation = (
    <div>
      {showValidateQuizAnswers() && (
        <ValidateQuizExamAnswers
          score={score}
          passingPercentage={passingPercentage}
          questions={questions}
          retryExam={canRetry ? retryExam : undefined}
          isRetrying={isPostingCreate}
          reviewQuestions={reviewQuestions}
          closePanel={closeExam}
        />
      )}
    </div>
  )

  const containerClasses = 'xl:container xl:mx-auto px-10 pt-7'

  // --- Fullscreen
  if (fullScreen) {
    return (
      <>
        {/* Desktop layout */}
        <div className='hidden sm:grid grid-cols-2 min-h-full'>
          <div className='bg-lightGrey'>
            <div className={containerClasses}>
              <div className='flex justify-end'>
                <div className='w-full max-w-[400px]'>
                  {heading}
                  {progressBar}
                  {buttons}
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className={containerClasses}>
              <div className='w-full max-w-[450px]'>
                {examContent}
                {answersValidation}
                <Divider size={DIVIDER_SIZE.BASE} />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile layout */}
        <div className='block sm:hidden'>
          <Container size={CONTAINER_STYLE.DESKTOP}>
            {heading}
            {progressBar}
            {examContent}
            {buttons}
            {answersValidation}
            <Divider size={DIVIDER_SIZE.BASE} />
          </Container>
        </div>
      </>
    )
  }

  // --- In panel
  return (
    <div>
      {progressBar}
      {examContent}
      {buttons}
      {answersValidation}
      <Divider size={DIVIDER_SIZE.BASE} />
    </div>
  )
}

export default QuizExamView
